import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { SongShakeFX, fxDebug, fxSupported } from '../_helpers/fxEngine.js';
import {
  ensureFxPackagesAndAlternatives,
  loadFxConfig,
  loadFxLoadout,
  photosensitiveFxPackage,
  resolveFxPackage,
  withOverrides,
} from '../_helpers/fxPackages.js';
import {
  prefersReducedMotion,
  reducePackage,
  reducedOverridesFor,
} from '../_helpers/fxReduceEffects.js';

/** Every event a stage's cards fire (`fx.on(name, fn)`; `ssfx:<name>` on the card element). */
export const FX_EVENTS = Object.freeze([
  'build-start', 'build-end',
  'boost-start', 'boost-release', 'boost-move', 'boost-end',
  'afterglow-start', 'afterglow-settled', 'afterglow-end',
  'vote', 'notice',
]);

/** Phones and tablets draw the canvases at 1× (ENG-804 performance). */
const MOBILE_QUERY = '(max-width: 700px), (pointer: coarse)';
const isMobile = () => {
  try {
    return Boolean(window.matchMedia?.(MOBILE_QUERY)?.matches);
  } catch (_e) {
    return false;
  }
};

/**
 * The SongShake FX stage for one page (ENG-804): one engine stage drawing on
 * the queue, shared through `FxContext` (`contexts/FxContext.jsx`) by
 * everything that adds cards or plays effects.
 *
 * `enabled` turns it on (the stream page: while the show is live). It reads
 * the FX config and this viewer's loadout, registers the packages, and once
 * the container is mounted calls `SongShakeFX.createStage` with:
 * - `container`: the element `containerRef` is on. The engine adds its two
 *   canvas layers inside it, so it must not cover the stream embed.
 * - `safeTop`: nothing is drawn above `getAvoid()`'s bottom edge (the stream
 *   player, looked up every frame since the player swaps its root element).
 * - `shakeEl`: the queue row (`rowRef`, which also needs the `ssfx-row` class).
 * - the viewer's vibe set and the rules package, both falling back to the
 *   defaults; `maxDpr: 1` on mobile.
 * It stops while the tab is hidden and is destroyed on unmount or when
 * `enabled` turns off. The engine itself honors `prefers-reduced-motion`
 * (no shake, tremble, push or speed lines); `reducedMotion` overrides it.
 *
 * `reduceEffects` is whether effects play reduced (ENG-810; ENG-836: the
 * Photosensitivity mode and more, `useReduceEffects`). While it is on, live
 * and without restarting the stage: every card's `boost()` plays its package through a per-viewer copy
 * without flash or lightning (`REDUCED_BOOST`), whoever calls it and with
 * whatever package; the vibe set loses its flicker and lightning
 * (`REDUCED_VIBE`, on top of any `setVibe` overrides); `packageFor` returns
 * reduced copies for 'boost' and 'vibe'; and the engine's reduced-motion
 * mode is on unless `reducedMotion` says otherwise. Off, the device's
 * reduced-motion setting applies as before.
 *
 * Photosensitivity alternatives (ENG-866): while `reduceEffects` is on, every
 * vibe set and boost shown (the viewer's own, a booster's, a boosted song's
 * per-card look) is replaced by the package's own photosensitive alternative,
 * else the default of its type (`photosensitiveFxPackage`), and then reduced
 * as above, so the defaults look exactly as they always have.
 *
 * Per-card vibe sets (ENG-866): a boosted song shows its booster's vibe set
 * (`card.setVibe`) until it plays, for every viewer.
 * - `setCardVibes(entries)` takes the queue (`[{entryId, boostedVibeId}]`,
 *   from the stream-page bootstrap) and is the base layer.
 * - `setBoostedVibe(entryId, vibeId)` records a live boost's vibe (the
 *   `fx_boost` frame) on top, until the queue catches up. It does not touch
 *   the card: the boost itself switches it at boost-end (`card.boost(pkg,
 *   {vibe})`), so the build keeps the viewer's look while it charges.
 * - `refreshCardVibe(entryId)` applies it now (a boost that ended or never
 *   played); `clearCardVibe(entryId)` forgets it (the song started).
 * A busy (boosting) card is never switched; cards created later (paged onto
 * the rail, a stage restart) start with theirs.
 *
 * Returns the controller FxContext provides:
 * - `status`: 'off' | 'loading' | 'ready' (a stage is drawing) | 'failed'.
 * - `stage`: the engine stage while ready, else null. For QA, `fxDebug()`
 *   also puts it on `window.songshakeFx` (`timeScale`, `debugBounds`).
 * - `engine`: the `SongShakeFX` API (`flip`, `energy`, `getPackage`, …).
 * - `rulesId`, `defaults` ({vibe, boost}), `loadout` ({vibe, boost}, resolved:
 *   every id in it is registered, or null for the engine's built-in look).
 * - `containerRef`, `rowRef`: callback refs for the stage and the queue row.
 * - `registerCard(id, {el, inner, cover, art})` → unregister: adds the card
 *   now or when the stage starts; `getCard(id)` → the engine card or null.
 * - `setLockedIds(ids)`: the cards that wear the locked (#1) look
 *   (`card.setLocked`, ENG-809); every other card is unlocked. Held here so a
 *   card created later (paged onto the rail, re-registered when its artwork
 *   arrives, or on a stage restart) starts in the right state.
 * - `on(name, fn)` → off: listens to card events across stage restarts.
 * - `setVibe(id, overrides?)`: switches this viewer's vibe set live
 *   (unknown → default; `overrides` passes a per-viewer copy).
 * - `resolvePackage(id, type)` → Promise of an id the engine knows (or null),
 *   fetching it first if needed: use it for a booster's boost animation or
 *   vibe set (in Photosensitivity mode, its alternative or the default).
 * - `setCardVibes`, `setBoostedVibe`, `refreshCardVibe`, `clearCardVibe`:
 *   per-card vibe sets, above.
 * - `packageFor(id, type, overrides?)`: sync; the id, the default, a
 *   per-viewer settings copy with `overrides` (and, for 'boost' and 'vibe'
 *   while `reduceEffects` is on, the reduce-effects overrides), or null.
 * - `reduceEffects`: whether this stage plays reduced effects.
 */
export const useFxStage = ({
  enabled = true,
  getAvoid = null,
  reducedMotion = null,
  reduceEffects = false,
} = {}) => {
  const supported = fxSupported();
  const active = Boolean(enabled && supported);
  const [assets, setAssets] = useState(null);
  const [status, setStatus] = useState('off');
  const [container, setContainer] = useState(null);
  const [stage, setStage] = useState(null);
  const rowElRef = useRef(null);
  const stageRef = useRef(null);
  const getAvoidRef = useRef(getAvoid);
  getAvoidRef.current = getAvoid;
  // id → { spec, card }: every card the page registered, staged or not.
  const cardsRef = useRef(new Map());
  // name → Set(fn)
  const listenersRef = useRef(new Map());
  // The vibe the viewer last picked (id + overrides), so a restarted stage keeps it.
  const vibeRef = useRef(null);
  const reduceRef = useRef(Boolean(reduceEffects));
  reduceRef.current = Boolean(reduceEffects);
  const assetsRef = useRef(null);
  assetsRef.current = assets;
  // Card ids that wear the locked (#1) look (`setLockedIds`).
  const lockedIdsRef = useRef(new Set());
  // Card id → its booster's vibe package: the queue's (base) and live boosts' (on top).
  const baseVibesRef = useRef(new Map());
  const liveVibesRef = useRef(new Map());

  /** In Photosensitivity mode, the package shown in place of `id` (ENG-866); else `id`. */
  const safeId = useCallback((id, type) => (
    reduceRef.current ? photosensitiveFxPackage(id, type, assetsRef.current?.defaults ?? null) : id
  ), []);

  /**
   * Puts a card's booster's vibe on it (or takes it off): substituted and
   * reduced as the viewer's settings say. A boosting card is left to its boost.
   * Keyed on the package that resolved, so one fetched later replaces its fallback.
   */
  const applyCardVibe = useCallback((card) => {
    if (typeof card?.setVibe !== 'function' || card.busy) return;
    const id = liveVibesRef.current.get(card.id) ?? baseVibesRef.current.get(card.id) ?? null;
    const resolved = id ? resolveFxPackage(safeId(id, 'vibe'), 'vibe', assetsRef.current?.defaults ?? null) : null;
    const key = resolved ? `${resolved}|${reduceRef.current ? 'reduced' : 'full'}` : null;
    if (key ? card.V && card.ssVibeKey === key : !card.V) return;
    card.ssVibeKey = key;
    if (!resolved) {
      card.setVibe(null);
      return;
    }
    card.setVibe(reduceRef.current ? withOverrides(resolved, 'vibe', reducedOverridesFor('vibe')) ?? resolved : resolved);
  }, [safeId]);

  /** The stage's vibe now: the picked (or loadout) vibe, its overrides, and reduce-effects. */
  const currentVibe = useCallback(() => {
    const own = vibeRef.current ?? { id: assetsRef.current?.loadout?.vibe ?? null, overrides: null };
    const picked = own.id ? { ...own, id: safeId(own.id, 'vibe') } : own;
    const reduced = reduceRef.current ? reducedOverridesFor('vibe') : null;
    const overrides = picked.overrides || reduced ? { ...(picked.overrides ?? {}), ...(reduced ?? {}) } : null;
    if (!overrides) return picked.id;
    // No package at all: the engine's built-in look, reduced through a plain settings object.
    if (!picked.id) return reduced ? { ...overrides } : null;
    return withOverrides(picked.id, 'vibe', overrides) ?? picked.id;
  }, [safeId]);

  /**
   * Readies a new engine card: its locked look from `setLockedIds`, and a
   * `boost()` that honors reduce-effects, read when each boost starts.
   */
  const armCard = useCallback((card) => {
    if (typeof card?.setLocked === 'function') card.setLocked(lockedIdsRef.current.has(card.id));
    applyCardVibe(card);
    if (!card || Object.prototype.hasOwnProperty.call(card, 'boost')) return card;
    const proto = Object.getPrototypeOf(card);
    if (typeof proto?.boost !== 'function') return card;
    card.boost = function boost(pkg, options) {
      return proto.boost.call(this, reduceRef.current ? reducePackage(pkg, 'boost') : pkg, options);
    };
    return card;
  }, [applyCardVibe]);

  // Config, loadout and packages, once per enable.
  useEffect(() => {
    if (!active) {
      setStatus('off');
      return undefined;
    }
    let cancelled = false;
    setStatus((s) => (s === 'ready' ? s : 'loading'));
    (async () => {
      const cfg = await loadFxConfig();
      const wanted = await loadFxLoadout(cfg.defaults);
      await ensureFxPackagesAndAlternatives([wanted.vibe, wanted.boost]);
      return {
        rulesId: cfg.rulesId,
        defaults: cfg.defaults,
        loadout: {
          vibe: resolveFxPackage(wanted.vibe, 'vibe', cfg.defaults),
          boost: resolveFxPackage(wanted.boost, 'boost', cfg.defaults),
        },
      };
    })().then(
      (next) => { if (!cancelled) setAssets(next); },
      () => {
        if (cancelled) return;
        setAssets(null);
        setStatus('failed');
      },
    );
    return () => { cancelled = true; };
  }, [active]);

  // The stage itself.
  useEffect(() => {
    if (!active || !assets || !container) return undefined;
    let fx;
    try {
      const vibe = currentVibe();
      const motion = reducedMotion ?? (reduceRef.current ? true : null);
      fx = SongShakeFX.createStage({
        container,
        safeTop: () => {
          const avoid = getAvoidRef.current?.();
          return avoid ? avoid.getBoundingClientRect().bottom - container.getBoundingClientRect().top + 1 : 0;
        },
        shakeEl: rowElRef.current,
        vibe,
        rules: assets.rulesId,
        maxDpr: isMobile() ? 1 : 2,
        ...(motion != null ? { reducedMotion: motion } : {}),
      });
    } catch (_e) {
      setStatus('failed');
      return undefined;
    }
    const offAll = fx.on('*', (name, detail) => {
      listenersRef.current.get(name)?.forEach((fn) => {
        try {
          fn(detail);
        } catch (err) {
          console.error(err);
        }
      });
    });
    cardsRef.current.forEach((entry) => {
      entry.card = armCard(fx.addCard(entry.spec));
    });
    const debug = fxDebug();
    if (debug.on) {
      if (debug.bounds) fx.debugBounds = true;
      window.songshakeFx = fx;
    }
    const onVisibility = () => {
      if (document.hidden) fx.stop();
      else fx.start();
    };
    document.addEventListener('visibilitychange', onVisibility);
    if (document.hidden) fx.stop();
    stageRef.current = fx;
    setStage(fx);
    setStatus('ready');
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      offAll?.();
      fx.destroy();
      cardsRef.current.forEach((entry) => {
        entry.card = null;
      });
      if (window.songshakeFx === fx) delete window.songshakeFx;
      stageRef.current = null;
      setStage(null);
    };
  }, [active, assets, container, reducedMotion, currentVibe, armCard]);

  // Reduce effects switched while a stage runs: applied live; a new stage reads it when created.
  useEffect(() => {
    const fx = stageRef.current;
    if (!fx) return;
    fx.reduced = reducedMotion ?? (reduceEffects ? true : prefersReducedMotion());
    fx.setVibe(currentVibe());
    cardsRef.current.forEach((entry) => applyCardVibe(entry.card));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduceEffects]);

  const containerRef = useCallback((node) => setContainer(node), []);
  const rowRef = useCallback((node) => {
    rowElRef.current = node;
    if (stageRef.current) stageRef.current.shakeEl = node;
  }, []);

  const registerCard = useCallback((id, spec) => {
    const cards = cardsRef.current;
    const previous = cards.get(id);
    if (previous?.card && stageRef.current) stageRef.current.removeCard(previous.card);
    const entry = { spec: { ...spec, id }, card: null };
    if (stageRef.current) entry.card = armCard(stageRef.current.addCard(entry.spec));
    cards.set(id, entry);
    return () => {
      if (cards.get(id) !== entry) return;
      cards.delete(id);
      if (entry.card && stageRef.current) stageRef.current.removeCard(entry.card);
      entry.card = null;
    };
  }, [armCard]);

  const getCard = useCallback((id) => cardsRef.current.get(id)?.card ?? null, []);

  const setLockedIds = useCallback((ids) => {
    const next = new Set(ids ?? []);
    lockedIdsRef.current = next;
    cardsRef.current.forEach((entry, id) => {
      const card = entry.card;
      if (typeof card?.setLocked !== 'function') return;
      const locked = next.has(id);
      if (Boolean(card.locked) !== locked) card.setLocked(locked);
    });
  }, []);

  const on = useCallback((name, fn) => {
    const map = listenersRef.current;
    if (!map.has(name)) map.set(name, new Set());
    map.get(name).add(fn);
    return () => map.get(name)?.delete(fn);
  }, []);

  const defaults = assets?.defaults ?? null;
  const packageFor = useCallback((id, type, overrides = null) => {
    const resolved = resolveFxPackage(id, type, defaults);
    if (!resolved) return null;
    const reduced = reduceEffects ? reducedOverridesFor(type) : null;
    const all = overrides || reduced ? { ...(overrides ?? {}), ...(reduced ?? {}) } : null;
    return all ? withOverrides(resolved, type, all) : resolved;
  }, [defaults, reduceEffects]);

  // A booster's package, fetched if needed; in Photosensitivity mode its alternative or the default.
  const resolvePackage = useCallback(
    (id, type) => ensureFxPackagesAndAlternatives([id], type).then(() => resolveFxPackage(safeId(id, type), type, defaults)),
    [defaults, safeId],
  );

  const applyVibesTo = useCallback((ids) => {
    ids.forEach((id) => applyCardVibe(cardsRef.current.get(id)?.card));
  }, [applyCardVibe]);

  const setCardVibes = useCallback((entries) => {
    const base = new Map();
    const present = new Set();
    (Array.isArray(entries) ? entries : []).forEach((e) => {
      if (e?.entryId == null) return;
      present.add(e.entryId);
      if (typeof e.boostedVibeId === 'string' && e.boostedVibeId) base.set(e.entryId, e.boostedVibeId);
    });
    // A live boost's vibe stays until the queue says the same, or its song leaves.
    liveVibesRef.current.forEach((vibe, id) => {
      if (!present.has(id) || base.get(id) === vibe) liveVibesRef.current.delete(id);
    });
    const touched = new Set([...baseVibesRef.current.keys(), ...base.keys()]);
    baseVibesRef.current = base;
    const ids = [...new Set(base.values())];
    return ensureFxPackagesAndAlternatives(ids, 'vibe').catch(() => {}).then(() => applyVibesTo(touched));
  }, [applyVibesTo]);

  const setBoostedVibe = useCallback((entryId, vibeId) => {
    if (entryId == null || typeof vibeId !== 'string' || !vibeId) return;
    liveVibesRef.current.set(entryId, vibeId);
  }, []);

  const refreshCardVibe = useCallback((entryId) => applyVibesTo([entryId]), [applyVibesTo]);

  const clearCardVibe = useCallback((entryId) => {
    liveVibesRef.current.delete(entryId);
    baseVibesRef.current.delete(entryId);
  }, []);

  const setVibe = useCallback((id, overrides = null) => {
    const resolved = resolveFxPackage(id, 'vibe', defaults);
    vibeRef.current = resolved ? { id: resolved, overrides } : null;
    const fx = stageRef.current;
    if (!fx) return resolved;
    fx.setVibe(currentVibe());
    return resolved;
  }, [defaults, currentVibe]);

  return useMemo(() => ({
    status: active ? status : 'off',
    stage,
    engine: SongShakeFX,
    rulesId: assets?.rulesId ?? null,
    defaults,
    loadout: assets?.loadout ?? null,
    containerRef,
    rowRef,
    registerCard,
    getCard,
    setLockedIds,
    on,
    setVibe,
    resolvePackage,
    packageFor,
    setCardVibes,
    setBoostedVibe,
    refreshCardVibe,
    clearCardVibe,
    reduceEffects: Boolean(reduceEffects),
  }), [active, status, stage, assets, defaults, containerRef, rowRef, registerCard, getCard, setLockedIds, on, setVibe, resolvePackage, packageFor, setCardVibes, setBoostedVibe, refreshCardVibe, clearCardVibe, reduceEffects]);
};

export default useFxStage;
